import { useState } from "react";
import { KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import LeadsTab from "@/components/admin/LeadsTab";
import PlansTab from "@/components/admin/PlansTab";
import GalleryTab from "@/components/admin/GalleryTab";
import AmenitiesTab from "@/components/admin/AmenitiesTab";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import HeroTab from "@/components/admin/HeroTab";
import SettingsTab from "@/components/admin/SettingsTab";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";

const TABS = [
    { id: "leads", label: "Leads" },
    { id: "plans", label: "Plans" },
    { id: "gallery", label: "Gallery" },
    { id: "amenities", label: "Amenities" },
    { id: "hero", label: "Hero" },
    { id: "analytics", label: "Analytics" },
    { id: "settings", label: "Settings" },
];

export default function AdminDashboard({ admin, onLogout }) {
    const [tab, setTab] = useState("leads");
    const [showPassword, setShowPassword] = useState(false);

    const logout = async () => {
        try {
            await api.post("/api/auth/logout");
        } catch {
            // token cleared client-side regardless
        }
        localStorage.removeItem("admin_token");
        toast.success("Logged out");
        onLogout();
    };

    return (
        <div className="min-h-screen bg-canvas" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-8">
                    <div className="flex items-center gap-3">
                        <img src="/assets/logo-full.png" alt="Work Island" className="h-10 w-auto" />
                        <span className="hidden rounded-full border border-brand-purple/20 bg-brand-purple/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-purple sm:inline-block">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <span className="hidden text-xs font-semibold text-ink/50 md:inline">{admin.email}</span>
                        <button
                            data-testid="change-password-btn"
                            onClick={() => setShowPassword(true)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-ink/70 transition-colors hover:border-ink/30 hover:text-ink"
                        >
                            <KeyRound size={13} strokeWidth={2.5} />
                            Change Password
                        </button>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-ink/70 transition-colors hover:border-red-300 hover:text-red-600"
                        >
                            <LogOut size={13} strokeWidth={2.5} />
                            Logout
                        </button>
                    </div>
                </div>
                <div className="mx-auto flex max-w-6xl gap-2 px-5 pb-3 sm:px-8">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`rounded-full px-5 py-2 text-[13px] font-bold transition-all duration-300 ${
                                tab === t.id
                                    ? "btn-gradient text-white"
                                    : "border border-slate-200 bg-white text-ink/55 hover:border-ink/25 hover:text-ink"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
                {tab === "leads" && <LeadsTab />}
                {tab === "plans" && <PlansTab />}
                {tab === "gallery" && <GalleryTab />}
                {tab === "amenities" && <AmenitiesTab />}
                {tab === "hero" && <HeroTab />}
                {tab === "analytics" && <AnalyticsTab />}
                {tab === "settings" && <SettingsTab />}
            </main>

            {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
        </div>
    );
}
